/**
 * Device answers as the app holds them. See docs/rules/api_boundary.md.
 */

import { z } from 'zod';

import type {
    DeviceDto, DeviceFrame, DeviceFramesDto, DiscoverAnswerDto, DiscoverResult, FlatDevice,
} from '@/types/devices';
import { DeviceDtoSchema, DeviceFramesDtoSchema, DiscoverAnswerDtoSchema } from '@/types/devices';

/** The cloud leaves out an empty list, an empty token, a zero rotation and a false flag. */
export function toDevice({ base, ...device }: DeviceDto): FlatDevice {
    return {
        ...base,
        ...device,
        enabledAgents: device.enabledAgents ?? [],
        blurCategories: device.blurCategories ?? [],
        availableProfiles: device.availableProfiles ?? [],
        primaryProfileToken: device.primaryProfileToken ?? '',
        secondaryProfileToken: device.secondaryProfileToken ?? '',
        rotationDegrees: device.rotationDegrees ?? 0,
        audioEnabled: device.audioEnabled ?? false,
        hasTwoWayAudio: device.hasTwoWayAudio ?? false,
    };
}

export const FlatDeviceSchema = DeviceDtoSchema.transform(toDevice);

const DeviceKeySchema = z.object({
    serverId: z.string(),
    base: z.object({ id: z.string() }),
});

/** A device row's key in a report: one device id has a row for each server it is on. */
export function getDeviceKey(row: unknown): string | null {
    const key = DeviceKeySchema.safeParse(row);
    return key.success ? `${key.data.serverId}/${key.data.base.id}` : null;
}

/**
 * Each sample as an `<img src>`. The batch may be shorter than the samples asked for, so a
 * caller placing frames on its own grid goes by `requestedTimestampMs`, not by position.
 */
export function toFrames(dto: DeviceFramesDto): DeviceFrame[] {
    return dto.frames.map((frame): DeviceFrame => ({
        requestedTimestampMs: frame.requestedTimestampMs,
        frameTimestampMs: frame.frameTimestampMs,
        url: `data:${frame.mimeType};base64,${frame.image}`,
    }));
}

export const DeviceFramesSchema = DeviceFramesDtoSchema.transform(toFrames);

/** The media_server wraps its probe outcomes in an envelope; callers get the array itself. */
export function discoverResultsFromDto(dto: DiscoverAnswerDto): DiscoverResult[] {
    return dto.results;
}

export const DiscoverResultsSchema = DiscoverAnswerDtoSchema.transform(discoverResultsFromDto);
